/**
 * The ONE shared drug results table (spec 2026-09-22 "one sortable,
 * filterable table"). Rendered by Keyword (KeywordSearchResults), which owns
 * its own sort/filter state and passes its (already-filtered) rows in. This component only sorts
 * a COPY of those rows for display; it never mutates them.
 */
import React, { useMemo, useState } from 'react';
import { regionDisplayLabel } from '../constants/filterConstants';
import {
  type DrugSort,
  type DrugSortColumn,
  type DrugTableFields,
  brandLabel,
  nextSort,
  sortDrugRows,
} from '../utils/drugResultsTable';
import { formatApprovalDate } from '../utils/approvalDate';
import { openInAppLink } from '../utils/openInNewTab';
import RefineChipBar from './RefineChipBar';
import type { RefineChip, RefineFields } from '../utils/refineQuery';

const BASE_COLUMNS: { label: string; column: DrugSortColumn }[] = [
  { label: 'Drug', column: 'brand' },
  { label: 'Manufacturer', column: 'manufacturer' },
  { label: 'Region', column: 'region' },
];

const APPROVAL_DATE_COLUMN: { label: string; column: DrugSortColumn } = {
  label: 'Approved',
  column: 'approval_date',
};

interface SortableHeaderProps {
  label: string;
  column: DrugSortColumn;
  sort: DrugSort | null;
  onSortChange: (next: DrugSort) => void;
}

function SortableHeader({ label, column, sort, onSortChange }: SortableHeaderProps) {
  const isActive = sort?.column === column;
  const direction = isActive ? sort.direction : null;
  return (
    <th
      className="py-2 pr-4 font-semibold"
      scope="col"
      {...(direction ? { 'aria-sort': direction === 'asc' ? 'ascending' : 'descending' } : {})}
    >
      <button
        type="button"
        onClick={() => onSortChange(nextSort(sort, column))}
        className="inline-flex items-center gap-1 hover:text-ink"
      >
        {label}
        {direction && <span aria-hidden="true">{direction === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

interface DrugResultsRowProps<T extends DrugTableFields> {
  row: T;
  usFdaVisible?: boolean;
  columnCount: number;
  showApprovalDate: boolean;
  showMatches: boolean;
  getBrandHref: (row: T) => string;
  onBrandClick: ((row: T, e: React.MouseEvent) => void) | undefined;
  canExpand: ((row: T) => boolean) | undefined;
  renderExpanded: ((row: T) => React.ReactNode) | undefined;
  renderMatches: ((row: T) => React.ReactNode) | undefined;
  selected: boolean;
  onToggleSelect: ((row: T) => void) | undefined;
  selectionFull: boolean;
}

function DrugResultsRow<T extends DrugTableFields>({
  row,
  usFdaVisible = false,
  columnCount,
  showApprovalDate,
  showMatches,
  getBrandHref,
  onBrandClick,
  canExpand,
  renderExpanded,
  renderMatches,
  selected,
  onToggleSelect,
  selectionFull,
}: DrugResultsRowProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const expandable = renderExpanded != null && (canExpand?.(row) ?? true);
  const expandLabel = `${expanded ? 'Collapse' : 'Expand'} ${brandLabel(row) ?? 'drug'} matches`;

  return (
    <>
      <tr>
        {onToggleSelect && (
          <td className="py-2 pl-3 pr-2">
            <input
              type="checkbox"
              className="h-4 w-4 accent-ink"
              aria-label={`Select ${brandLabel(row) ?? 'drug'}`}
              checked={selected}
              disabled={!selected && selectionFull}
              title={!selected && selectionFull ? 'Compare holds up to 5 drugs' : undefined}
              onChange={() => onToggleSelect(row)}
            />
          </td>
        )}
        <td className="py-2 pr-4">
          <div className="flex flex-col gap-0.5">
            <a
              href={getBrandHref(row)}
              onClick={(e) => (onBrandClick ? onBrandClick(row, e) : openInAppLink(e, getBrandHref(row)))}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[15px] font-semibold text-accent hover:text-accent-deep hover:underline"
            >
              {brandLabel(row) ?? 'Unknown Drug'}
            </a>
            {row.brand_name && row.generic_name && <span className="text-[13px] text-ink-2">{row.generic_name}</span>}
          </div>
        </td>
        <td className="py-2 pr-4 text-ink-2">{row.manufacturer ?? '—'}</td>
        <td className="py-2 pr-4 font-mono text-[12px] text-ink-2">{row.region ? regionDisplayLabel(row.region, usFdaVisible) : '—'}</td>
        {showApprovalDate && (
          <td className="py-2 pr-4 font-mono text-[12px] text-ink-2">
            {formatApprovalDate(row.approval_date, row.approval_date_precision) ?? '—'}
          </td>
        )}
        {showMatches && (
          <td className="py-2 pr-4 text-right font-mono text-[13px] font-medium text-ink">
            {renderMatches ? renderMatches(row) : row.total_matches}
          </td>
        )}
        {renderExpanded && (
          <td className="py-2 pr-4">
            {expandable && (
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                aria-label={expandLabel}
                title={expandLabel}
                aria-expanded={expanded}
                className="inline-flex h-8 w-8 items-center justify-center text-ink-2 hover:text-ink rounded transition-colors"
              >
                <svg
                  className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
          </td>
        )}
      </tr>
      {renderExpanded && expandable && expanded && (
        <tr>
          <td colSpan={columnCount} className="px-[52px] pb-4">{renderExpanded(row)}</td>
        </tr>
      )}
    </>
  );
}

export interface DrugResultsTableProps<T extends DrugTableFields> {
  /** US-Drugs@FDA visible: the US region reads "US-DailyMed". */
  usFdaVisible?: boolean;
  /** Already filtered by the view; this table sorts a COPY for display. */
  rows: T[];
  sort: DrugSort | null;
  onSortChange: (next: DrugSort) => void;
  rowKey: (row: T) => React.Key;
  getBrandHref: (row: T) => string;
  /** Replaces the brand link's plain-click handler (the href stays for
   *  modified / middle clicks). */
  onBrandClick?: ((row: T, e: React.MouseEvent) => void) | undefined;
  /** Whether the Approval Date column exists at all — header AND every row's
   *  cell, never a per-row check. Approval dates are reliable for every
   *  region (2026-10-15 backfill), so every caller now passes true except a
   *  surface that intentionally omits the column (e.g. DrugDetailSidebar's
   *  compact search panel, which has no room for it). */
  showApprovalDate: boolean;
  /** Keyword: adds the Matches column (row.total_matches). */
  showMatches?: boolean | undefined;
  /** Keyword: which rows get the expand chevron. */
  canExpand?: ((row: T) => boolean) | undefined;
  /** Keyword: the expanded panel; the trailing column exists only when set. */
  renderExpanded?: ((row: T) => React.ReactNode) | undefined;
  /** Rows arrive already in display order (server sort): skip the client sort. */
  presorted?: boolean | undefined;
  /** Overrides the Matches cell content (default: row.total_matches). */
  renderMatches?: ((row: T) => React.ReactNode) | undefined;
  /** Optional row selection (Keyword → Compare); the checkbox column exists
   *  only when onToggleSelect is set. selectionFull disables unticked boxes. */
  selectedKeys?: ReadonlySet<React.Key> | undefined;
  onToggleSelect?: ((row: T) => void) | undefined;
  selectionFull?: boolean | undefined;
}

export default function DrugResultsTable<T extends DrugTableFields>({
  rows,
  usFdaVisible = false,
  sort,
  onSortChange,
  rowKey,
  getBrandHref,
  onBrandClick,
  showApprovalDate,
  showMatches,
  canExpand,
  renderExpanded,
  presorted,
  renderMatches,
  selectedKeys,
  onToggleSelect,
  selectionFull,
}: DrugResultsTableProps<T>) {
  // When Approval Date is hidden, a stale sort on it (e.g. carried over from
  // a prior US-only search) must not apply — no header shows aria-sort/arrow,
  // and rows keep backend order. Derived, not stored: the parent's sort state
  // is untouched, so switching back to a US-only scope restores it.
  const effectiveSort = showApprovalDate || sort?.column !== 'approval_date' ? sort : null;
  const columns = showApprovalDate ? [...BASE_COLUMNS, APPROVAL_DATE_COLUMN] : BASE_COLUMNS;
  const sorted = useMemo(
    () => (presorted ? rows : sortDrugRows(rows, effectiveSort)),
    [rows, effectiveSort, presorted],
  );
  const columnCount = columns.length + (showMatches ? 1 : 0) + (renderExpanded ? 1 : 0) + (onToggleSelect ? 1 : 0);

  return (
    <div className="overflow-x-auto bg-white border border-line rounded">
      <table className="min-w-full divide-y divide-line text-sm">
        <thead>
          <tr className="bg-ground text-left text-[12px] font-semibold text-ink-2">
            {onToggleSelect && (
              <th scope="col" className="py-2 pl-3 pr-2">
                <span className="sr-only">Select</span>
              </th>
            )}
            {columns.map(({ label, column }) => (
              <SortableHeader
                key={column}
                label={label}
                column={column}
                sort={effectiveSort}
                onSortChange={onSortChange}
              />
            ))}
            {showMatches && (
              <SortableHeader label="Matches" column="matches" sort={effectiveSort} onSortChange={onSortChange} />
            )}
            {renderExpanded && <th className="py-2 pr-4" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-line-subtle">
          {sorted.map((row) => (
            <DrugResultsRow
              key={rowKey(row)}
              usFdaVisible={usFdaVisible}
              row={row}
              columnCount={columnCount}
              showApprovalDate={showApprovalDate}
              showMatches={!!showMatches}
              getBrandHref={getBrandHref}
              onBrandClick={onBrandClick}
              canExpand={canExpand}
              renderExpanded={renderExpanded}
              renderMatches={renderMatches}
              selected={selectedKeys?.has(rowKey(row)) ?? false}
              onToggleSelect={onToggleSelect}
              selectionFull={!!selectionFull}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface ResultsFilterBoxProps {
  chips: RefineChip[];
  onChipsChange: (chips: RefineChip[]) => void;
  /** The current page's rows through `drugTableRefineFields` — the chip bar
   *  is the only filter here, so unlike Cogni this is every loaded row. */
  baseRows: RefineFields[];
  shownCount: number;
  totalCount: number;
  showPageNote: boolean;
  usFdaVisible?: boolean;
}

export function ResultsFilterBox({ chips, onChipsChange, baseRows, shownCount, totalCount, showPageNote, usFdaVisible = false }: ResultsFilterBoxProps) {
  return (
    <div className="mb-3 flex items-center gap-3 flex-wrap">
      <div className="flex-1 min-w-[320px]">
        <RefineChipBar
          chips={chips}
          onChipsChange={onChipsChange}
          baseRows={baseRows}
          placeholder="Filter by brand, generic, manufacturer, region, year or text"
          ariaLabel="Filter results"
          usFdaVisible={usFdaVisible}
        />
      </div>
      {chips.length > 0 && (
        <span className="text-sm text-ink-3">
          Showing {shownCount} of {totalCount} on this page
        </span>
      )}
      {showPageNote && <span className="text-xs text-ink-3">Sorted / filtered within this page</span>}
    </div>
  );
}

export interface FilterNoMatchesProps {
  onClear: () => void;
}

export function FilterNoMatches({ onClear }: FilterNoMatchesProps) {
  return (
    <div className="text-center py-8 text-sm text-ink-2">
      No results on this page match these filters
      <button type="button" onClick={onClear} className="ml-2 text-accent hover:text-accent-deep hover:underline">
        Clear
      </button>
    </div>
  );
}

export interface ResultsPaginationProps {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

// Moved verbatim from KeywordSearchResults.tsx (pre-unify) — Page X of Y,
// Previous, numbered pages with ellipsis, and Next.
export function ResultsPagination({ currentPage, totalCount, pageSize, onPageChange }: ResultsPaginationProps) {
  if (totalCount <= pageSize) return null;
  const totalPages = Math.ceil(totalCount / pageSize);
  return (
    <div className="flex items-center justify-between mt-6 pt-4 border-t border-line">
      <p className="text-sm text-ink-3">
        Page {currentPage} of {totalPages}
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="px-3 py-1.5 text-sm font-medium rounded-lg border transition-all disabled:opacity-40 disabled:cursor-not-allowed border-line text-ink-2 hover:border-accent-ring hover:text-accent hover:bg-accent-tint"
        >
          Previous
        </button>
        {/* Page number buttons */}
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
          .reduce<(number | 'ellipsis')[]>((acc, p, i, arr) => {
            if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('ellipsis');
            acc.push(p);
            return acc;
          }, [])
          .map((item, i) =>
            item === 'ellipsis' ? (
              <span key={`ellipsis-${i}`} className="px-1 text-ink-3">...</span>
            ) : (
              <button
                key={item}
                onClick={() => onPageChange(item as number)}
                className={`w-8 h-8 text-sm font-medium rounded-lg transition-all ${
                  currentPage === item
                    ? 'bg-accent text-white'
                    : 'text-ink-2 hover:bg-accent-tint hover:text-accent'
                }`}
              >
                {item}
              </button>
            )
          )}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="px-3 py-1.5 text-sm font-medium rounded-lg border transition-all disabled:opacity-40 disabled:cursor-not-allowed border-line text-ink-2 hover:border-accent-ring hover:text-accent hover:bg-accent-tint"
        >
          Next
        </button>
      </div>
    </div>
  );
}
